/**
 * THE ONE DEFINITION OF "A WORD", AND THE ONE DEFINITION OF "HOW LONG IS THIS TEXT".
 *
 * WHY IT IS HERE AND NOT IN `src/`. `adr/0008`: "`shared/content-model` becomes load-bearing:
 * the block union types, the `blocks ⇄ editor JSON` converter, the D18 anchor maths, and the client
 * mirror of **all eight of D21's rules** that CORE also enforces server-side." `adr/0008` is the
 * discipline this file exists to keep - "One anchor implementation, two callers." The word count has
 * THREE: `04-schema.md` names them, "one function - whitespace-split tokens over title +
 * subtitle + every block's plain `body` + `quote_attribution`, NFC-normalised - shared by the
 * editor, the submit check and the moderation worker."
 *
 * WHAT GOES WRONG IF THERE ARE TWO. It is not hypothetical; it is the defect D21 was written to end.
 * `03-worksheet.md`: "The legacy had two different counts that disagreed with each other,
 * so an author could see '1,203 words ✓' on screen and be rejected at 1,198." The two were the
 * moderation server counting whitespace tokens over MARKDOWN and the web editor counting them over
 * PLAIN TEXT (`research/19-block-content-anchors-research.md`, WEB-127/234/371). `WC-CR-11` was
 * ruled **(a)** - one function, plain text, both sides.
 *
 * THE DEFINITION, VERIFIED AGAINST THE CORPUS BEFORE IT WAS CODED. Four places state it and they
 * agree word for word:
 *   - `07-api/authors-api.yaml` (submitArticle): "The word count is our own definition -
 *     whitespace-split tokens over title, subtitle, every block's plain body and each quote
 *     attribution, NFC-normalised".
 *   - `07-api/authors-api.yaml` (`StructuralRules.wordCountDefinition`), which exists so
 *     "the number the editor shows and the number the submit guard uses must be one function".
 *   - `07-api/authors-api.yaml` (`WorkingCopy.wordCount`), same sentence.
 *   - `04-schema.md` and `03-worksheet.md`, the ruling itself.
 *
 * TWO DEVIATIONS FROM THE LEGACY NUMBER, both ruled and both deliberate, so nobody "fixes" them:
 *   - **A bare web address stops being a word.** There is no markdown in `body`
 *     (`04-schema.md`), so a link is its anchor text and nothing else.
 *   - **Image captions and quote attributions start being words.** An image block's caption is held
 *     in that block's own `body` (`authors-api.yaml`: "Held in the block's own text.
 *     Captions count as words, which they did not in the legacy editor"), and the attribution is
 *     counted by name (`authors-api.yaml`). `03-worksheet.md` records the
 *     consequence the first draft got wrong: the count is **not** always lower than the legacy's -
 *     "an image-heavy article can now count higher".
 * `image_alt` is NOT counted. It is a separate column (`04-schema.md`) and appears in none of
 * the four statements of the definition; alt text is an accessibility attribute of the image, not
 * prose the reader reads.
 *
 * CHINESE AND JAPANESE (design 29, W3). They are written without spaces, so there a whitespace
 * token is a whole sentence, and a 3,000-character article counted a handful of words - under D21's
 * 300-word floor for ever. So inside a token each Han, Hiragana or Katakana character is one word,
 * as word processors count them, and CJK punctuation (、。「」) is none. A text with no such
 * character counts exactly as before. Not `Intl.Segmenter`: its answer depends on the ICU build,
 * and the cabinet and the server must agree to the word.
 *
 * ★ WHAT IS **NOT** RULED, AND IS RECORDED RATHER THAN INVENTED. The corpus fixes what a word is and
 * never fixes what a CHARACTER is for D21's title (10-70), subtitle (10-100) and quote (400/100)
 * bounds. `countTextLength` below takes Unicode code points and the three candidates are argued at
 * its own header. The eight-name rule vocabulary those bounds are reported under IS ruled -
 * `authors-api.yaml` - and is not this file's to restate.
 *
 * C11. This file reads no clock and takes no instant. Nothing here is time-dependent, which is why
 * it does not carry `ids.ts`'s `now` parameter.
 */

/**
 * The token separator: one or more JavaScript `\s`.
 *
 * INHERITED, NOT CHOSEN. `WC-CR-11` (a) keeps "the server definition, computed over plain text, and
 * the client mirrors that same function", and the plain-text counter the ruling points at is the
 * legacy web editor's, recorded verbatim at `research/19-block-content-anchors-research.md`:
 * `editor.getText().trim().split(/\s+/).filter(Boolean).length`. So the separator is that one, and
 * the split that skips empty tokens below is that expression with `trim()` made unnecessary.
 *
 * WHAT `\s` COVERS AND WHY IT MATTERS: it includes U+00A0 NO-BREAK SPACE, the U+2000-U+200A spaces,
 * U+3000 IDEOGRAPHIC SPACE and U+FEFF - all of which arrive by paste from Word and from the web. A
 * hand-rolled `split(' ')` would count "one word" for a line pasted with non-breaking spaces and
 * refuse a 400-word article as 40 words.
 */
const WHITESPACE_RUN = /\s+/;

/**
 * A Chinese or Japanese character: Han, Hiragana or Katakana, plus the kana marks Unicode files
 * under Common - the prolonged sound mark ー (and half-width ｰ) and the vertical repeat marks
 * 〱-〵. CJK punctuation is Common too, and is deliberately not here.
 */
const CJK_CHARACTER =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\u3031-\u3035\u30FC\uFF70]/u;

/** What makes a run of other characters inside a CJK token a word: a letter or a digit. */
const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u;

/**
 * Words in one whitespace token. Without a CJK character it is one word, as it always was. With
 * them, each CJK character is a word and so is each run between them holding a letter or digit:
 * "MoonXの記事" is 4, "2026年" is 2, "「本」、" is 1.
 */
function countWordsInToken(token: string): number {
  if (!CJK_CHARACTER.test(token)) {
    return 1;
  }
  // n CJK characters cut the token into n + 1 runs of everything else, most of them empty.
  const runs = token.split(CJK_CHARACTER);
  return runs.length - 1 + runs.filter((run) => LETTER_OR_DIGIT.test(run)).length;
}

/**
 * One block, as far as counting is concerned - and it deliberately carries no `kind`.
 *
 * The database already guarantees the shape per kind, so the counter does not have to branch on it:
 * `ck_awb_divider` forces every one of `body`, `text_sha256`, `quote_attribution`, `file_key`,
 * `image_alt` to NULL on a divider, and `ck_awb_text` / `ck_awb_image` force `quote_attribution`
 * NULL on text and image blocks (verified live against `article_working_block`, and declared at
 * `04-schema.md`). So "every block's body plus each quote attribution" is exactly
 * "`body ?? ''` and `quoteAttribution ?? ''` over every block", with no kind test that could drift
 * from the CHECKs.
 *
 * BOTH MEMBERS ARE REQUIRED AND NULLABLE, never optional. `tsconfig.json` sets
 * `exactOptionalPropertyTypes`, and a caller who forgets a member should be told by the compiler
 * rather than silently counting an attribution as zero words.
 */
export interface CountableBlock {
  readonly body: string | null;
  readonly quoteAttribution: string | null;
}

/** A document to count: the two head fields and the blocks, in no particular order. */
export interface CountableArticle {
  readonly title: string;
  readonly subtitle: string;
  readonly blocks: readonly CountableBlock[];
}

/**
 * Words in ONE piece of text, NFC-normalised first: whitespace-split tokens, each one word unless
 * it holds Chinese or Japanese characters (`countWordsInToken`).
 *
 * NORMALISE, THEN SPLIT - in that order, because the ruling says "NFC-normalised" of the text being
 * counted. It changes no token boundary in practice (NFC composes marks; it never turns whitespace
 * into a letter or the reverse) but the order is what the words say, and `countTextLength` below
 * depends on the same order for a reason where it very much does change the answer.
 *
 * Skipping empty tokens is load-bearing: `''.split(/\s+/)` is `['']`, and a leading or trailing
 * space yields an empty leading or trailing member. Without it an empty subtitle counts as one word.
 */
export function countWordsInText(text: string): number {
  let words = 0;
  for (const token of text.normalize('NFC').split(WHITESPACE_RUN)) {
    if (token.length > 0) {
      words += countWordsInToken(token);
    }
  }
  return words;
}

/**
 * THE word count. `article_working_copy.word_count` and `article_version.word_count` hold what this
 * function returned, and `04-schema.md` makes those columns "the structural-limit
 * enforcement inputs" - so this function is what D21's 300-7,000 band is measured with, everywhere.
 *
 * SUMMED PER FIELD RATHER THAN OVER ONE JOINED STRING, and the two are equal. A whitespace-run token
 * cannot span a field boundary if the fields are joined by whitespace, so
 * `countWordsInText([title, subtitle, ...bodies].join('\n'))` returns this number - proved as a
 * property in the spec, not asserted here. Summing is preferred because it allocates nothing: a
 * 7,000-word article is ~45 KB and this runs on the autosave path.
 *
 * WHAT IT DOES **NOT** DO: it does not read the database, and it does not know that `word_count` is
 * a stored column. The writer of that column calls this; the submit-time guard reads the column the
 * writer left (`04-schema.md`). If some future save path computes the number some other
 * way, the guard checks a fiction and no test in this file can see it - which is exactly why
 * `research/18-editor-review-flow-data-research.md` rules `word_count` "a plain column, not a
 * generated one": "One definition, one writer, stored as an ordinary integer."
 */
export function countWords(article: CountableArticle): number {
  let total = countWordsInText(article.title) + countWordsInText(article.subtitle);
  for (const block of article.blocks) {
    if (block.body !== null) {
      total += countWordsInText(block.body);
    }
    if (block.quoteAttribution !== null) {
      total += countWordsInText(block.quoteAttribution);
    }
  }
  return total;
}

/**
 * How long a piece of text is, for D21's title / subtitle / quote bounds: NFC-normalised Unicode
 * CODE POINTS.
 *
 * ★ THE UNIT IS NOT RULED ANYWHERE AND THIS IS A CHOICE. Recorded as such rather than dressed in a
 * citation. Three units were on the table and here is why this one:
 *
 *   - **Code points - taken.** `04-schema.md` writes the quote bound as "quote 400/100
 *     CHARACTERS", and this corpus draws "character" against "grapheme" in exactly one place, at
 *     `research/26-authors-identity-application-research.md`: "`char_length` counts characters,
 *     not bytes ... the ruled limits came from an iOS field that counts grapheme clusters". So
 *     "characters" here is `char_length`, and PostgreSQL's `char_length` counts code points. The
 *     second reason is internal consistency: `adr/0008` (Q31b) already fixes code points as the
 *     unit for in-block offsets in this very package - "measured in Unicode code points over the
 *     block's stored plain text". Measuring an offset in one unit and a length in another, over the
 *     same string, in the same package, is how the next bug gets written.
 *   - **UTF-16 code units (`String.prototype.length`) - rejected.** `research/19:843` records
 *     `"😄".length === 2` and `"𞤲𞥋𞤣𞤫".length === 8` against 4. A `title.length <= 70` check refuses
 *     titles the ruled number admits, and it would disagree with the database's own `char_length`
 *     the moment anyone writes a backstop CHECK.
 *   - **Grapheme clusters (`Intl.Segmenter`) - rejected HERE, and it is ruled elsewhere.**
 *     `S4-OC40` and `S4-OD15` rule graphemes for the PROFILE text limits, counted in the API before
 *     the insert. Those are a different guard with a different refusal:
 *     `06-oracles/transition-vectors.md` gives profile bounds `PROFILE_LIMIT_EXCEEDED` "as
 *     `STRUCTURAL_LIMITS_FAILED` uses for articles", and
 *     `src/common/guards/transitions/structural_limits.ts` names the profile bounds as the
 *     thing this guard does not cover. Borrowing the profile's unit for D21 would be borrowing a
 *     ruling made about a different field.
 *
 * NORMALISE FIRST, AND HERE IT CHANGES THE ANSWER. "é" as U+0065 U+0301 is two code points; NFC
 * composes it to U+00E9, one. `WD-134` (`03-worksheet.md`) puts NFC-on-write for every text
 * column in this same package, so a title that reached the database through the ruled path is
 * already NFC and this call is a no-op - but `article_working_copy.title` and `.subtitle` carry NO
 * `IS NFC NORMALIZED` CHECK (verified live; only `article_working_block.body` has one,
 * `ck_awb_body_nfc`), so the no-op is not guaranteed by the database and normalising here is what
 * makes the measurement independent of which path wrote the row.
 */
export function countTextLength(text: string): number {
  // Spreading a string iterates it by code point - that is the whole point of the spread here, and
  // `text.normalize('NFC').length` (UTF-16 units) is the bug it is written to avoid.
  return [...text.normalize('NFC')].length;
}

/**
 * How long a title or subtitle is for D21's bounds: `countTextLength` without trailing whitespace.
 * A space typed at the end is invisible, so it must not push a title over its maximum or help it
 * reach its minimum (the owner, 19 October 2026). Only the count ignores it; the text is kept as
 * typed. The editor's checklist and the submit guard both call this, so they keep agreeing.
 */
export function countTitleLength(text: string): number {
  return countTextLength(text.trimEnd());
}
